'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { HeaderLogo } from '@eliadmin/core/src/components/header/header-logo';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { BiSolidBuildings } from 'react-icons/bi';
import { FaFacebookF, FaInstagram, FaYoutube } from 'react-icons/fa';
import { MdMailOutline, MdOutlineCall } from 'react-icons/md';
import { TbGridDots } from 'react-icons/tb';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug ?? '#'} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left text-xl font-semibold',
              layer === 1 && 'text-[16px] text-lg font-medium',
              layer && layer > 1 && 'text-sm font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();

  const categories = props.categories;

  const followData = [
    {
      icon: <FaFacebookF size={24} />,
      value: 'Facebook',
      link: generalLayout.social_media?.facebook,
    },
    {
      icon: <FaInstagram size={24} />,
      value: 'Instagram',
      link: generalLayout.social_media?.instagram,
    },
    {
      icon: <FaYoutube size={24} />,
      value: 'Youtube',
      link: generalLayout.social_media?.youtube,
    },
  ];

  const companyData = [
    {
      icon: <MdOutlineCall size={24} />,
      value: generalLayout.contact?.phone,
    },
    {
      icon: <MdMailOutline size={24} />,
      value: generalLayout.contact?.email,
    },
    {
      icon: <BiSolidBuildings size={24} />,
      value: `${generalLayout.contact?.address},
      ${generalLayout.contact?.postcode} ${generalLayout.contact?.city}`,
    },
  ];

  return (
    <>
      <Sheet>
        <SheetTrigger aria-label="Menu"
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <TbGridDots size={26} className="text-black" />
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          side={props.side}
          className={cn(
            'flex w-screen flex-col items-start justify-start gap-8 overflow-scroll bg-[#F4F5FB] sm:w-[390px]',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-6 top-11',
            icon: 'text-black h-8 w-8 font-bold bg-white rounded-full p-1.5',
          }}
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <HeaderLogo generalLayout={generalLayout} />
          <div className="flex lg:hidden">
            {!!props.menu && (
              <div className="flex flex-col gap-2">
                {props.menu?.map((item, index) => {
                  const Comp = item.path
                    ? isRelativeHref(getSafeHref(item.path))
                      ? Link
                      : 'a'
                    : 'span';
                  return (
                    <SheetClose key={index} asChild>
                      <Comp
                        href={item.path ? getSafeHref(item.path) : (undefined as any)}
                        className="flex w-full"
                        prefetch={false}
                      >
                        <div className={cn('text-sm', '')}>{item.name}</div>
                      </Comp>
                    </SheetClose>
                  );
                })}
              </div>
            )}
          </div>
          <Separator />
          <div className="w-full space-y-4">
            <span>{t('text492')}</span>
            {followData.map(item => (
              <Link prefetchOnHover
                href={item.link}
                target="_blank"
                key={item.link}
                prefetch={false}
                className="flex items-center justify-start gap-3 text-lg font-medium"
              >
                <div className="flex h-[56px] w-[56px] items-center justify-center bg-primary text-black">
                  {item.icon}
                </div>
                {item.value}
              </Link>
            ))}
          </div>
          <Separator />
          <div className="w-full space-y-4">
            <span>{t('text612')}</span>
            {companyData.map(item => (
              <div
                key={item.value}
                className="flex items-center justify-start gap-3 text-lg font-medium"
              >
                <div className="flex h-[56px] w-[56px] min-w-[56px] items-center justify-center bg-primary text-black">
                  {item.icon}
                </div>
                {item.value}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
